import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import React from 'react';

import CopyIcon from '@/assets/icons/copy.svg?react';
import { PaymentInstructionsResponse } from '@/DTOs/paymentInstruction';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';
import { LabelAndValue } from '@/types/labelAndValue';

interface Props {
  readonly paymentInstructions: PaymentInstructionsResponse;
}

export const FiatAccountDetailsContent: React.FC<Props> = (props: Props): React.ReactElement => {
  const { paymentInstructions } = props;
  const tr = useTranslator();
  const toast = useToast();

  const copyText = React.useCallback(
    (text: string, message: string): void => {
      const { clipboard } = navigator;
      clipboard.writeText(text).then((): void => {
        toast.show(message, 'success');
      });
    },
    [toast],
  );

  const onCopyAccountNumber = React.useCallback((): void => {
    if (paymentInstructions?.accountNumber) {
      copyText(paymentInstructions.accountNumber, tr('ACCOUNT_NUMBER_COPIED'));
    }
  }, [paymentInstructions?.accountNumber, copyText, tr]);

  const onCopyRoutingNumber = React.useCallback((): void => {
    if (paymentInstructions?.routingNumber) {
      copyText(paymentInstructions.routingNumber, tr('ROUTING_NUMBER_COPIED'));
    }
  }, [paymentInstructions?.routingNumber, copyText, tr]);

  const onCopyReference = React.useCallback((): void => {
    if (paymentInstructions?.reference) {
      copyText(paymentInstructions.reference, tr('REFERENCE_COPIED'));
    }
  }, [paymentInstructions?.reference, copyText, tr]);

  const items = React.useMemo(
    (): readonly LabelAndValue<string | React.ReactElement>[] =>
      paymentInstructions
        ? [
            {
              label: tr('BENEFICIARY_NAME'),
              value: paymentInstructions.receiverName,
            },
            ...(paymentInstructions.receiverAddress
              ? [
                  {
                    label: tr('BENEFICIARY_ADDRESS'),
                    value: paymentInstructions.receiverAddress,
                  },
                ]
              : []),
            {
              label: tr('BANK_INFORMATION'),
              value: (
                <Box>
                  <Typography variant="body2">{paymentInstructions.receiverBankName}</Typography>
                  <Typography variant="body2" color="custom.LightGray">
                    {paymentInstructions.receiverBankAddress}
                  </Typography>
                </Box>
              ),
            },
            {
              label: tr('ACCOUNT_NUMBER_WITH_CURRENCY', CurrencyCodeType.USD),
              value: (
                <Box display="flex" alignItems="center">
                  <Typography variant="body2">{paymentInstructions.accountNumber}</Typography>
                  <Box height="1em" display="flex" alignItems="center">
                    <IconButton color="primary" size="small" onClick={onCopyAccountNumber}>
                      <CopyIcon fill="currentColor" width={16} height={16} />
                    </IconButton>
                  </Box>
                </Box>
              ),
            },
            {
              label: tr('ROUTING_NUMBER_WITH_CURRENCY', CurrencyCodeType.USD),
              value: (
                <Box display="flex" alignItems="center">
                  <Typography variant="body2">{paymentInstructions.routingNumber}</Typography>
                  <Box height="1em" display="flex" alignItems="center">
                    <IconButton color="primary" size="small" onClick={onCopyRoutingNumber}>
                      <CopyIcon fill="currentColor" width={16} height={16} />
                    </IconButton>
                  </Box>
                </Box>
              ),
            },
            ...(paymentInstructions.swiftCode
              ? [
                  {
                    label: tr('SWIFT_CODE'),
                    value: paymentInstructions.swiftCode,
                  },
                ]
              : []),
            ...(paymentInstructions.reference
              ? [
                  {
                    label: tr('REFERENCE'),
                    value: (
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2">{paymentInstructions.reference}</Typography>
                        <Box height="1em" display="flex" alignItems="center">
                          <IconButton color="primary" size="small" onClick={onCopyReference}>
                            <CopyIcon fill="currentColor" width={16} height={16} />
                          </IconButton>
                        </Box>
                      </Box>
                    ),
                  },
                ]
              : []),
          ]
        : [],
    [paymentInstructions, onCopyAccountNumber, onCopyRoutingNumber, onCopyReference, tr],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      toast.remove();
    };
  }, [toast]);

  return (
    <Box sx={styles.grid}>
      {items.map((item: LabelAndValue<string | React.ReactElement>): React.ReactElement => {
        return (
          <Box key={item.label}>
            <Typography sx={styles.sectionTitle} variant="h7" color="custom.LightGray">
              {item.label}
            </Typography>
            {typeof item.value === 'string' ? (
              <Typography variant="body2">{item.value}</Typography>
            ) : (
              item.value
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const styles: Record<string, any> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 3,
  },
  sectionTitle: {
    mb: 0.5,
  },
};
